import { useState } from 'react';
import { Card, Tabs, Table, Button, Modal, Form, Input, Select, message, Space, Tag, Collapse, Tooltip, Spin } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, SendOutlined, InfoCircleOutlined, MailOutlined } from '@ant-design/icons';
import { useQuery, useMutation } from 'react-query';
import { mailingAPI } from '../api/endpoints';
import { formatDate, getTemplateTypeLabel, getEmailStatusColor, getEmailStatusLabel } from '../utils/helpers';

const WILDCARDS = [
  {
    group: 'Cliente',
    items: [
      { variable: '{{customer_name}}',    description: 'Nombre o razón social del cliente' },
      { variable: '{{customer_contact}}', description: 'Nombre del contacto del cliente' },
    ],
  },
  {
    group: 'Factura (consolidado)',
    items: [
      { variable: '{{invoices_table}}',       description: 'Tabla HTML con el detalle de todas las facturas que activaron la regla (folio, servicio, período, monto, pendiente, vencimiento)' },
      { variable: '{{invoice_count}}',        description: 'Número de facturas incluidas en el correo' },
      { variable: '{{total_remaining_amount}}', description: 'Suma del monto pendiente de todas las facturas' },
      { variable: '{{total_amount}}',         description: 'Suma del monto total de todas las facturas' },
    ],
  },
  {
    group: 'Factura (individual / compatible)',
    items: [
      { variable: '{{invoice_number}}',   description: 'Folio(s) de factura — si hay varias, se listan separados por coma' },
      { variable: '{{amount}}',           description: 'Monto total — suma consolidada si hay varias facturas' },
      { variable: '{{remaining_amount}}', description: 'Monto pendiente — suma consolidada si hay varias facturas' },
      { variable: '{{due_date}}',          description: 'Fecha(s) de vencimiento — si hay varias, se listan separadas por coma' },
      { variable: '{{payment_deadline}}',     description: 'Fecha límite de pago más próxima (due_date + días de gracia)' },
      { variable: '{{grace_days_remaining}}', description: 'Días restantes hasta la fecha límite más próxima' },
      { variable: '{{days_remaining}}',       description: 'Días restantes mínimos entre todas las facturas' },
      { variable: '{{days_overdue}}',         description: 'Días de atraso máximos entre todas las facturas' },
      { variable: '{{service_description}}', description: 'Descripción(es) del servicio — si hay varias distintas, se listan separadas por coma' },
    ],
  },
  {
    group: 'Periodo del servicio',
    items: [
      { variable: '{{service_period}}',      description: 'Periodo legible, ej: "enero 2026" o "enero – febrero 2026"' },
      { variable: '{{service_period_from}}', description: 'Fecha de inicio del periodo (YYYY-MM-DD)' },
      { variable: '{{service_period_to}}',   description: 'Fecha de fin del periodo (YYYY-MM-DD)' },
    ],
  },
];

const MailingPage = () => {
  const [activeTab, setActiveTab] = useState('templates');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form] = Form.useForm();

  // Preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Send test state
  const [sendTestOpen, setSendTestOpen] = useState(false);
  const [sendTestId, setSendTestId] = useState(null);
  const [sendTestLoading, setSendTestLoading] = useState(false);
  const [testForm] = Form.useForm();

  // View email body state
  const [bodyOpen, setBodyOpen] = useState(false);
  const [bodyData, setBodyData] = useState(null);
  const [bodyLoading, setBodyLoading] = useState(false);

  const { data: templatesData, refetch: refetchTemplates } = useQuery('email-templates', () => mailingAPI.listTemplates());
  const { data: scheduledData } = useQuery('scheduled-emails', () => mailingAPI.listScheduled());
  const { data: logsData } = useQuery('email-logs', () => mailingAPI.listLogs({ ordering: '-sent_date' }));

  const templates = templatesData?.data?.results || [];
  const scheduled = scheduledData?.data?.results || [];
  const logs = logsData?.data?.results || [];

  const createTemplateMutation = useMutation(mailingAPI.createTemplate);
  const updateTemplateMutation = useMutation(({ id, data }) => mailingAPI.updateTemplate(id, data));
  const deleteTemplateMutation = useMutation(mailingAPI.deleteTemplate);

  const handleViewBody = async (record) => {
    setBodyLoading(true);
    setBodyOpen(true);
    setBodyData(null);
    try {
      const response = await mailingAPI.getLog(record.id);
      setBodyData(response.data);
    } catch (error) {
      message.error('Error al cargar el cuerpo del correo');
      setBodyOpen(false);
    } finally {
      setBodyLoading(false);
    }
  };

  const handlePreview = async (record) => {
    setPreviewLoading(true);
    setPreviewOpen(true);
    try {
      const response = await mailingAPI.previewTemplate(record.id, {});
      setPreviewData(response.data);
    } catch (error) {
      message.error('Error al generar la previsualización');
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSendTest = async (values) => {
    setSendTestLoading(true);
    try {
      const response = await mailingAPI.sendTestEmail(sendTestId, { email: values.email });
      message.success(response.data.message);
      setSendTestOpen(false);
      testForm.resetFields();
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Error al enviar correo de prueba';
      message.error(errorMsg);
    } finally {
      setSendTestLoading(false);
    }
  };

  const templateColumns = [
    {
      title: 'Nombre',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Tipo',
      dataIndex: 'template_type',
      key: 'template_type',
      render: (type) => getTemplateTypeLabel(type),
    },
    {
      title: 'Asunto',
      dataIndex: 'subject',
      key: 'subject',
    },
    {
      title: 'Activa',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active) => (active ? 'Sí' : 'No'),
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => handlePreview(record)} />
          <Button
            size="small"
            icon={<SendOutlined />}
            onClick={() => {
              setSendTestId(record.id);
              testForm.resetFields();
              setSendTestOpen(true);
            }}
          />
          <Button type="primary" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Button type="primary" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)} />
        </Space>
      ),
    },
  ];

  const scheduledColumns = [
    {
      title: 'Destinatario',
      dataIndex: 'recipient_email',
      key: 'recipient_email',
    },
    {
      title: 'Asunto',
      dataIndex: 'subject',
      key: 'subject',
    },
    {
      title: 'Programado Para',
      dataIndex: 'scheduled_date',
      key: 'scheduled_date',
      render: (date) => formatDate(date),
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getEmailStatusColor(status)}>
          {getEmailStatusLabel(status)}
        </Tag>
      ),
    },
  ];

  const logsColumns = [
    {
      title: 'Cliente',
      dataIndex: 'customer_name',
      key: 'customer_name',
      ellipsis: true,
    },
    {
      title: 'Factura',
      dataIndex: 'invoice_number',
      key: 'invoice_number',
      render: (num) => num || '—',
    },
    {
      title: 'Destinatario',
      dataIndex: 'recipient_email',
      key: 'recipient_email',
      ellipsis: true,
    },
    {
      title: 'Asunto',
      dataIndex: 'subject',
      key: 'subject',
      ellipsis: true,
    },
    {
      title: 'Fecha Envío',
      dataIndex: 'sent_date',
      key: 'sent_date',
      render: (date) => formatDate(date),
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getEmailStatusColor(status)}>
          {getEmailStatusLabel(status)}
        </Tag>
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => (
        <Tooltip title="Ver cuerpo del correo">
          <Button
            size="small"
            icon={<MailOutlined />}
            onClick={() => handleViewBody(record)}
            disabled={!record.html_content}
          />
        </Tooltip>
      ),
    },
  ];

  const handleEdit = (record) => {
    setEditingId(record.id);
    form.setFieldsValue(record);
    setIsModalOpen(true);
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: 'Eliminar Plantilla',
      content: '¿Está seguro que desea eliminar esta plantilla?',
      okText: 'Sí',
      cancelText: 'No',
      onOk: async () => {
        try {
          await deleteTemplateMutation.mutateAsync(id);
          message.success('Plantilla eliminada correctamente');
          refetchTemplates();
        } catch (error) {
          message.error('Error al eliminar plantilla');
        }
      },
    });
  };

  const onFinish = async (values) => {
    try {
      if (editingId) {
        await updateTemplateMutation.mutateAsync({ id: editingId, data: values });
        message.success('Plantilla actualizada correctamente');
      } else {
        await createTemplateMutation.mutateAsync(values);
        message.success('Plantilla creada correctamente');
      }
      setIsModalOpen(false);
      form.resetFields();
      setEditingId(null);
      refetchTemplates();
    } catch (error) {
      message.error('Error al guardar plantilla');
    }
  };

  const tabItems = [
    {
      key: 'templates',
      label: 'Plantillas',
      children: (
        <Card
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingId(null);
                form.resetFields();
                setIsModalOpen(true);
              }}
            >
              Nueva Plantilla
            </Button>
          }
        >
          <Table dataSource={templates} columns={templateColumns} rowKey="id" />
        </Card>
      ),
    },
    {
      key: 'scheduled',
      label: 'Correos Programados',
      children: <Table dataSource={scheduled} columns={scheduledColumns} rowKey="id" />,
    },
    {
      key: 'logs',
      label: 'Historial de Correos',
      children: <Table dataSource={logs} columns={logsColumns} rowKey="id" />,
    },
  ];

  return (
    <div>
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />

      {/* Modal crear/editar plantilla */}
      <Modal
        title={editingId ? 'Editar Plantilla' : 'Nueva Plantilla'}
        open={isModalOpen}
        onOk={() => form.submit()}
        onCancel={() => setIsModalOpen(false)}
        okText="Guardar"
        cancelText="Cancelar"
        width={800}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            name="name"
            label="Nombre"
            rules={[{ required: true, message: 'Por favor ingresa el nombre' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="template_type"
            label="Tipo"
            rules={[{ required: true, message: 'Por favor selecciona un tipo' }]}
          >
            <Select>
              <Select.Option value="reminder">Recordatorio</Select.Option>
              <Select.Option value="overdue">Vencimiento</Select.Option>
              <Select.Option value="payment_received">Pago Recibido</Select.Option>
              <Select.Option value="custom">Personalizado</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="subject"
            label="Asunto"
            rules={[{ required: true, message: 'Por favor ingresa el asunto' }]}
          >
            <Input placeholder="Usa {{invoice_number}}, {{customer_name}}, etc." />
          </Form.Item>

          <Collapse
            size="small"
            style={{ marginBottom: 16 }}
            items={[{
              key: 'wildcards',
              label: <span><InfoCircleOutlined style={{ marginRight: 6 }} />Variables disponibles (comodines)</span>,
              children: (
                <div>
                  <p style={{ marginBottom: 12, color: '#595959', fontSize: 12 }}>
                    Haz clic en cualquier variable para copiarla al portapapeles y pégala en el asunto o en el contenido HTML.
                  </p>
                  {WILDCARDS.map(({ group, items }) => (
                    <div key={group} style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#8c8c8c', textTransform: 'uppercase', marginBottom: 6 }}>
                        {group}
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {items.map(({ variable, description }) => (
                          <Tooltip key={variable} title={description}>
                            <Tag
                              color="blue"
                              style={{ cursor: 'pointer', fontFamily: 'monospace', fontSize: 13 }}
                              onClick={() => {
                                navigator.clipboard.writeText(variable);
                                message.success(`${variable} copiado al portapapeles`);
                              }}
                            >
                              {variable}
                            </Tag>
                          </Tooltip>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ),
            }]}
          />

          <Form.Item
            name="html_content"
            label="Contenido HTML"
            rules={[{ required: true, message: 'Por favor ingresa el contenido' }]}
          >
            <Input.TextArea rows={10} placeholder="Contenido HTML de la plantilla..." />
          </Form.Item>

          <Form.Item name="text_content" label="Contenido de Texto Plano">
            <Input.TextArea rows={5} />
          </Form.Item>

          <Form.Item
            name="is_active"
            label="Activa"
            initialValue={true}
          >
            <Select>
              <Select.Option value={true}>Sí</Select.Option>
              <Select.Option value={false}>No</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal previsualización */}
      <Modal
        title={previewData ? `Previsualización: ${previewData.subject}` : 'Previsualización'}
        open={previewOpen}
        onCancel={() => {
          setPreviewOpen(false);
          setPreviewData(null);
        }}
        footer={null}
        width={700}
      >
        <Spin spinning={previewLoading}>
          <iframe
            title="preview"
            srcDoc={previewData?.html_content ?? ''}
            sandbox="allow-same-origin"
            style={{ width: '100%', height: 500, border: '1px solid #d9d9d9', borderRadius: 4 }}
          />
        </Spin>
      </Modal>

      {/* Modal envío de prueba */}
      <Modal
        title="Enviar Correo de Prueba"
        open={sendTestOpen}
        onCancel={() => {
          setSendTestOpen(false);
          setSendTestId(null);
        }}
        footer={null}
      >
        <p style={{ color: '#666', marginBottom: 16 }}>
          Se enviará la plantilla con datos de ejemplo al email indicado. El asunto incluirá el prefijo [PRUEBA].
        </p>
        <Form form={testForm} layout="vertical" onFinish={handleSendTest}>
          <Form.Item
            name="email"
            label="Email destinatario"
            rules={[
              { required: true, message: 'Ingresa un email' },
              { type: 'email', message: 'Ingresa un email válido' },
            ]}
          >
            <Input placeholder="ejemplo@correo.com" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={sendTestLoading} icon={<SendOutlined />}>
              Enviar Correo de Prueba
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal ver cuerpo del correo */}
      <Modal
        title={bodyData ? `Correo: ${bodyData.subject}` : 'Cuerpo del correo'}
        open={bodyOpen}
        onCancel={() => {
          setBodyOpen(false);
          setBodyData(null);
        }}
        footer={null}
        width={750}
      >
        <Spin spinning={bodyLoading}>
          {bodyData && (
            <div style={{ marginBottom: 12, color: '#595959', fontSize: 13 }}>
              <strong>Cliente:</strong> {bodyData.customer_name}
              {bodyData.invoice_number && (
                <span style={{ marginLeft: 16 }}>
                  <strong>Factura:</strong> {bodyData.invoice_number}
                </span>
              )}
              <span style={{ marginLeft: 16 }}>
                <strong>Destinatario:</strong> {bodyData.recipient_email}
              </span>
            </div>
          )}
          <iframe
            title="email-body"
            srcDoc={bodyData?.html_content ?? ''}
            sandbox="allow-same-origin"
            style={{ width: '100%', height: 500, border: '1px solid #d9d9d9', borderRadius: 4 }}
          />
        </Spin>
      </Modal>
    </div>
  );
};

export default MailingPage;
